import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShoppingCart } from "lucide-react";
import { format } from "date-fns";

interface ProductOrder {
    order_id: string;
    quantity: number;
    created_at: string;
    order_number: string;
    customer_name: string;
    customer_email: string;
    status: string;
    total_amount: number;
}

export const ProductOrders = ({ productId }: { productId: string }) => {
    const [orders, setOrders] = useState<ProductOrder[]>([]);
    const [loading, setLoading] = useState(true);

    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    useEffect(() => {
        fetchOrders();
    }, [productId]);

    const fetchOrders = async () => {
        try {
            console.log('Fetching orders for product:', productId);
            const { data, error } = await supabase
                .from('sub_orders')
                .select(`
          quantity,
          order_id,
          orders (
            id,
            order_number,
            created_at,
            status,
            total_amount,
            user_id
          )
        `)
                .eq('product_id', productId)
                .order('created_at', { ascending: false });

            if (error) throw error;

            console.log('Orders fetch result:', data);

            // Fetch profiles separately for better performance/reliability
            const ordersWithProfiles = await Promise.all(
                (data || []).map(async (item: any) => {
                    let customerName = 'Guest';
                    let customerEmail = '';

                    // Try to fetch profile if user_id exists
                    if (item.orders?.user_id) {
                        const { data: profile } = await supabase
                            .from('profiles')
                            .select('first_name, last_name, email')
                            .eq('id', item.orders.user_id)
                            .maybeSingle();

                        if (profile) {
                            customerName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'No Name';
                            customerEmail = profile.email || '';
                        }
                    }

                    return {
                        order_id: item.orders?.id || 'N/A',
                        quantity: item.quantity,
                        created_at: item.orders?.created_at || new Date().toISOString(),
                        order_number: item.orders?.order_number || 'Unknown',
                        customer_name: customerName,
                        customer_email: customerEmail,
                        status: item.orders?.status || 'pending',
                        total_amount: item.orders?.total_amount || 0
                    };
                })
            );

            setOrders(ordersWithProfiles);
        } catch (error: any) {
            console.error('Error fetching product orders:', error);
            setErrorMsg(error.message || 'Error fetching orders');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'paid': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'cancelled': return 'bg-red-100 text-red-800';
            case 'processing': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'paid': return 'שולם';
            case 'pending': return 'ממתין';
            case 'cancelled': return 'בוטל';
            case 'processing': return 'בטיפול';
            default: return status;
        }
    };

    if (loading) {
        return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
    }

    if (errorMsg) {
        return (
            <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6 text-red-600">
                    <p>שגיאה בטעינת הזמנות: {errorMsg}</p>
                </CardContent>
            </Card>
        );
    }

    if (orders.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <ShoppingCart className="h-5 w-5" />
                        הזמנות מקושרות
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground text-center py-4">
                        לא נמצאו הזמנות למוצר זה
                        <br />
                        <span className="text-xs font-mono text-gray-400">ID: {productId}</span>
                    </p>
                </CardContent>
            </Card>
        );
    }

    const totalSold = orders.reduce((sum, order) => sum + order.quantity, 0);

    return (
        <Card>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <ShoppingCart className="h-5 w-5" />
                        הזמנות מקושרות
                    </CardTitle>
                    <Badge variant="secondary" className="text-sm">
                        סה"כ נמכרו: {totalSold} יח'
                    </Badge>
                </div>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-right">מספר הזמנה</TableHead>
                                <TableHead className="text-right">לקוח</TableHead>
                                <TableHead className="text-right">תאריך</TableHead>
                                <TableHead className="text-center">כמות</TableHead>
                                <TableHead className="text-center">סטטוס</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {orders.map((order) => (
                                <TableRow key={order.order_id}>
                                    <TableCell className="font-medium">#{order.order_number}</TableCell>
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <span>{order.customer_name}</span>
                                            <span className="text-xs text-muted-foreground">{order.customer_email}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>{format(new Date(order.created_at), 'dd/MM/yyyy HH:mm')}</TableCell>
                                    <TableCell className="text-center font-bold">{order.quantity}</TableCell>
                                    <TableCell className="text-center">
                                        <Badge variant="outline" className={getStatusColor(order.status)}>
                                            {getStatusLabel(order.status)}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};
